import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import {
  createMirror,
  cleanImageResourceUrl,
  deleteURLParameters,
  getLockValue,
  getSettingsDataInstance,
  getShadowFabricInstance,
  removeObjectFromCanvas,
  removeRepeatingLinkParams,
  setURLParameters,
  isURL,
} from '@frontend/utils';
import { fabric } from 'fabric';
import { FIELD_TYPE_BACKGROND_THEME_IMAGE } from '@frontend/constants/type-fields-of-template';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import { maskFabricBuilder } from '@frontend/group/modules/import-template-from-psd/mask-generator';
import {DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES} from "@frontend/group/modules/additional-fields";
import {getGroupNameByAddFieldId} from "@/common/preview/draw-smart-object";
import {setSrcAsync} from "@frontend/lib/fabric-helper";
import { fitStoreBackgroundToCanvas } from '@frontend/components/display-builder/store-background';

const getScopePage = () => ShowPageScopeCollector.get(['getIsLightweight', 'reorderObjectCanvas', 'canvas_data', 'updateLayoutChanges']);
const PASTE_OBJECT_OFFSET = 15;

function getSmartObjectFrame(canvas, groupName) {
  if (!groupName) {
    return null;
  }

  return canvas
    .getObjects()
    .find(obj => obj.groupName === groupName && obj.clipPathArea);
}

function getSmartObjectElementIdByGroupName(data, groupName) {
  if (!data || !groupName) {
    return null;
  }

  return Object.keys(data)
    .find(key => key.startsWith('smart_object_') && key.endsWith('_group_name') && data[key] === groupName)
    ?.replace('_group_name', '') ?? null;
}

function getSmartObjectBounds({ data, smartObjectCoords, groupName, smartObjectFrame }) {
  if (smartObjectFrame) {
    return smartObjectFrame;
  }

  const coords = groupName ? smartObjectCoords?.[groupName] : null;
  if (!coords) {
    return null;
  }

  const elementId = getSmartObjectElementIdByGroupName(data, groupName);

  return {
    left: Number(coords.X),
    top: Number(coords.Y),
    width: Number(coords.Width ?? coords.width ?? data?.[`${elementId}_width`]),
    height: Number(coords.Height ?? coords.height ?? data?.[`${elementId}_height`]),
    scaleX: Number(coords.scaleX) || 1,
    scaleY: Number(coords.scaleY) || 1,
  };
}

function isPointInsideFrame(x, y, frame) {
  if (!frame) {
    return false;
  }

  const left = Number(frame.left);
  const top = Number(frame.top);
  const width = Number(frame.width) * (Number(frame.scaleX) || 1);
  const height = Number(frame.height) * (Number(frame.scaleY) || 1);

  return x >= left && x <= left + width && y >= top && y <= top + height;
}

function getAdditionalBackgroundPosition({ left, top, offsetX, offsetY, smartObjectFrame }) {
  const absolutePosition = {
    left: left + offsetX,
    top: top + offsetY,
  };

  if (!smartObjectFrame) {
    return absolutePosition;
  }

  const groupX = Number(smartObjectFrame.left) || 0;
  const groupY = Number(smartObjectFrame.top) || 0;
  const relativePosition = {
    left: absolutePosition.left + groupX,
    top: absolutePosition.top + groupY,
  };

  if (isPointInsideFrame(absolutePosition.left, absolutePosition.top, smartObjectFrame)) {
    return absolutePosition;
  }

  if (isPointInsideFrame(relativePosition.left, relativePosition.top, smartObjectFrame)) {
    return relativePosition;
  }

  return absolutePosition;
}

function getImageDimensions(url) {
  return new Promise((resolve, reject) => {
    fabric.Image.fromURL(
      url,
      (oImg) => {
        if (!oImg) {
          reject(new Error('Failed to load image'));
          return;
        }

        resolve({
          width: oImg.width,
          height: oImg.height,
        });
      }
    );
  });
}

export async function setAdditionalBackgroundImage({ data, canvas, id, shadows, dimension, isApplyToAll , isNeedReRenderCanvas = true}, options = {}) {
  const additionalBackground = data['additional_background_images'];
  if (!additionalBackground) {
    canvas.backgroundImage = null;
    if (isNeedReRenderCanvas) {
      canvas.renderAll();
    }
    return Promise.resolve();
  }

  const { canvas_data, getIsLightweight, reorderObjectCanvas, updateLayoutChanges } = getScopePage();
  const is_lightweight = getIsLightweight();

  let urls = [];
  if (Array.isArray(additionalBackground)) {
    urls = additionalBackground.map(url => cleanImageResourceUrl(Array.isArray(url) ? url[0] : url));
  } else {
    urls.push(cleanImageResourceUrl(additionalBackground));
  }

  // replace domain name from URL POR-2222
  urls.forEach((u, i) => {
    if (urls[i] && urls[i].includes('share?file=')) {
      urls[i] = cleanImageResourceUrl(removeRepeatingLinkParams(urls[i], '&full_resolution'));
    }
  });

  removeObjectFromCanvas(canvas, ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID);

  const promises = [];

  canvas_data[id].additionalBkImgCoords = [];
  
  const imageSizes = await Promise.all(
    // empty slots (field without an image) are skipped below, don't try to load them
    canvas_data[id].additional_background_theme_image.entries().map(async ([i]) => (urls[i] ? getImageDimensions(urls[i]) : null))
  )
  
  const settingsTemplate = getSettingsDataInstance(data?.settings_template);

  for (const [i, b] of canvas_data[id].additional_background_theme_image.entries()) {

    if (!urls[i]) {
      continue;
    }

    const objectName = b.name || `Additional Background Theme Image ${i + 1}`;
    const dataWidth = data.add_bk_img_width?.[i] ?? canvas_data[id]._data.add_bk_img_width?.[i];
    const dataHeight = data.add_bk_img_height?.[i] ?? canvas_data[id]._data.add_bk_img_height?.[i];

    const storedObjectFit = data.add_bk_img_object_fit?.[i];
    const storedObjectFill = data.add_bk_img_object_fill?.[i];
    const displayBuilderLayerRole = (
      data.add_bk_img_display_builder_meta?.[i]?.role
      ?? canvas_data[id]._data.add_bk_img_display_builder_meta?.[i]?.role
    );
    const isDisplayBuilderLayer = Boolean(
      window.isDisplayBuilder
      && ['rear', 'foreground', 'store'].includes(displayBuilderLayerRole)
    );
    const preserveDimensions = isDisplayBuilderLayer && Boolean(
      data.add_bk_img_preserve_dimensions?.[i]
        ?? canvas_data[id]._data.add_bk_img_preserve_dimensions?.[i]
    );
    const isAnotherInstance = Boolean(
      data.add_bk_img_is_another_instance?.[i]
      ?? canvas_data[id]._data.add_bk_img_is_another_instance?.[i]
    );
    const shouldUseStoredDimensions = Boolean(
      preserveDimensions
      || isAnotherInstance
      || storedObjectFit?.align_to_fit
      || storedObjectFill?.align_to_fill
    );

    let dimension_width = (shouldUseStoredDimensions && dataWidth) ? dataWidth : b['width'];
    let dimension_height = (shouldUseStoredDimensions && dataHeight) ? dataHeight : b['height'];
    // With stored dimensions the saved scale is relative to add_bk_img_width/height, also for a
    // pasted image: normalizeTransferredAdditionalBackgroundImageSize() saves a resized image pasted
    // from another template as width/height = its visual size and scale = 1. (POR-14773)
    const isScaleRelativeToStoredSize = Boolean(shouldUseStoredDimensions && dataWidth && dataHeight);
    
    const offset_x = parseFloat(data.add_bk_img_offset_x[i]);
    const offset_y = parseFloat(data.add_bk_img_offset_y[i]);

    const opacity = parseFloat(data.add_bk_img_opacity?.[i] || 1);
  
    let left = b['left'] + dimension[4].x;
    let top = b['top'] + dimension[4].y;

    const objectId = ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID + i;
    let fieldsOrderReassignedByUser = canvas_data[id]._data.users_fields_order;
    fieldsOrderReassignedByUser = fieldsOrderReassignedByUser ? JSON.parse(canvas_data[id]._data.users_fields_order) : false;
    const order = fieldsOrderReassignedByUser ? fieldsOrderReassignedByUser.indexOf(objectId) + 1 : b['order'];
    const visible = data.add_bk_img_visible?.[i] ?? true;
    const isClippingMask = data.add_bk_img_is_clipping_mask?.[i] ?? false;
    const mirrorShadow = data?.add_bk_img_mirror_shadows?.[i]?.length ? data?.add_bk_img_mirror_shadows?.[i] : null;
    const mirrorShadowGroupId = data?.add_bk_img_mirror_shadow_group_ids?.length ? data?.add_bk_img_mirror_shadow_group_ids?.[i] : null;
    
    const imageSize = imageSizes[i];
    
    const promise = new Promise((resolve) => {
      let url = urls[i];
      let original_url = url;
   
      //
      const baseWidth = Number(data.add_bk_img_width?.[i] ?? canvas_data[id]._data.add_bk_img_width?.[i] ?? dimension_width) || dimension_width;
      const baseHeight = Number(data.add_bk_img_height?.[i] ?? canvas_data[id]._data.add_bk_img_height?.[i] ?? dimension_height) || dimension_height;

      let scale = canvas_data[id]._data.add_bk_img_scale?.[i] ?? 1;
      let scale_y = canvas_data[id]._data.add_bk_img_scale_y?.[i] ?? scale;
      
      if (!Array.isArray(data.add_bk_img_object_fit)) {
        data.add_bk_img_object_fit = [];
      }

      // For fitting and filling we will use the current width and height set on the UI 

      // the way scales are calculated for this field seem to differ between width and height, for example doing baseHeight * scale_y 
      // I get the value to match the UI but not with the width, I need to calculate the dimension_width like im doing below
      
      let calculated_dimension_width = 0;
      if (dimension_height) {
          if (imageSize?.width && imageSize?.height) {
            calculated_dimension_width = dimension_height / (imageSize.height / imageSize.width);
          }
      }

      const ui_width  = calculated_dimension_width * scale;
      const ui_height = baseHeight * scale_y;

      const preserve_fit = storedObjectFit?.preserve_fit ?? false;
      
      const fit_width = preserve_fit ? (storedObjectFit?.width_to_fit ?? ui_width) : (ui_width ?? storedObjectFit?.width_to_fit);
      const fit_height = preserve_fit ? (storedObjectFit?.height_to_fit ?? ui_height) : (ui_height ?? storedObjectFit?.height_to_fit);

      const objectFitSettings = {
        width_to_fit: Math.round(fit_width || baseWidth),
        height_to_fit: Math.round(fit_height || baseHeight),
        align_to_fit: storedObjectFit?.align_to_fit ?? '',
        preserve_fit
      };

      data.add_bk_img_object_fit[i] = {
        ...objectFitSettings,
      };

      canvas_data[id]._data.add_bk_img_object_fit = data.add_bk_img_object_fit;

      if (!Array.isArray(data.add_bk_img_object_fill)) {
        data.add_bk_img_object_fill = [];
      }

      const preserve_fill = storedObjectFill?.preserve_fill ?? false;

      const fill_width = preserve_fill ? (storedObjectFit?.width_to_fill ?? ui_width) : (ui_width ?? storedObjectFit?.width_to_fill);
      const fill_height = preserve_fill ? (storedObjectFit?.height_to_fill ?? ui_height) : (ui_height ?? storedObjectFit?.height_to_fill);

      const objectFillSettings = {
        width_to_fill: Math.round(fill_width || baseWidth),
        height_to_fill: Math.round(fill_height || baseHeight),
        align_to_fill: storedObjectFill?.align_to_fill ?? '',
        objectFillCrop: storedObjectFill?.objectFillCrop ?? true,
        imageOffset: storedObjectFill?.imageOffset,
        preserve_fill
      };

      data.add_bk_img_object_fill[i] = {
        ...objectFillSettings,
      };

      canvas_data[id]._data.add_bk_img_object_fill = data.add_bk_img_object_fill;

      const objectFitSettingsRef = data.add_bk_img_object_fit[i];
      const objectFillSettingsRef = data.add_bk_img_object_fill[i];
      const groupName = getGroupNameByAddFieldId(
        objectId,
        canvas_data[id]
      );
      const hasValidAlignment = (value) => typeof value === 'string' && value.includes(',');

      const initObjectFillOrFit = () => {
        if (url && isURL(url)) {
          url = deleteURLParameters(url, [
            'width_to_fit',
            'height_to_fit',
            'align_to_fit',
            'width_to_fill',
            'height_to_fill',
            'align_to_fill',
            'full_resolution',
            'objectFillCrop',
          ]);
        }

        if (dimension_width && dimension_height && hasValidAlignment(objectFitSettingsRef.align_to_fit)) {
          url = setURLParameters(url, {
            width_to_fit: objectFitSettingsRef.width_to_fit,
            height_to_fit: objectFitSettingsRef.height_to_fit,
            align_to_fit: objectFitSettingsRef.align_to_fit,
          });
          scale = 1;
          scale_y = 1;
          if (objectFitSettingsRef.width_to_fit) {
            dimension_width = objectFitSettingsRef.width_to_fit;
          }
          if (objectFitSettingsRef.height_to_fit) {
            dimension_height = objectFitSettingsRef.height_to_fit;
          }
        }

        if (
          dimension_width &&
          dimension_height &&
          hasValidAlignment(objectFillSettingsRef.align_to_fill) &&
          objectFillSettingsRef.objectFillCrop
        ) {
          url = setURLParameters(url, {
            width_to_fill: objectFillSettingsRef.width_to_fill,
            height_to_fill: objectFillSettingsRef.height_to_fill,
            align_to_fill: objectFillSettingsRef.align_to_fill,
            objectFillCrop: objectFillSettingsRef.objectFillCrop ? 'true' : 'false',
          });
          scale = 1;
          scale_y = 1;
          if (objectFillSettingsRef.width_to_fill) {
            dimension_width = objectFillSettingsRef.width_to_fill;
          }
          if (objectFillSettingsRef.height_to_fill) {
            dimension_height = objectFillSettingsRef.height_to_fill;
          }
        }
      };

      const applyObjectFillWithoutCrop = (imageObject) => {
        const align = objectFillSettingsRef.align_to_fill;
        if (
          !hasValidAlignment(align) ||
          objectFillSettingsRef.objectFillCrop ||
          !objectFillSettingsRef.width_to_fill ||
          !objectFillSettingsRef.height_to_fill
        ) {
          return;
        }

        const [horizontalAlign, verticalAlign] = align.split(',');
        const targetAspectRatio = objectFillSettingsRef.width_to_fill / objectFillSettingsRef.height_to_fill;
        const originalWidth = imageObject.width;
        const originalHeight = imageObject.height;

        imageObject.setPositionByOrigin({ x: left, y: top }, 'left', 'top');

        if (originalWidth / originalHeight > targetAspectRatio) {
          imageObject.scale(objectFillSettingsRef.height_to_fill / originalHeight);
        } else {
          imageObject.scale(objectFillSettingsRef.width_to_fill / originalWidth);
        }

        const newWidth = imageObject.getScaledWidth();
        const newHeight = imageObject.getScaledHeight();

        const offset = objectFillSettingsRef.imageOffset ?? { x: 0, y: 0 };
        const initialPoint = { x: 0, y: 0 };

        switch (horizontalAlign) {
          case 'left':
            initialPoint.x = imageObject.left + ((newWidth - objectFillSettingsRef.width_to_fill) / 2);
            imageObject.left = initialPoint.x + offset.x;
            break;
          case 'center':
            initialPoint.x = imageObject.left;
            imageObject.left = imageObject.left + offset.x;
            break;
          case 'right':
            initialPoint.x = imageObject.left - ((newWidth - objectFillSettingsRef.width_to_fill) / 2);
            imageObject.left = initialPoint.x + offset.x;
            break;
          default:
            break;
        }

        switch (verticalAlign) {
          case 'top':
            initialPoint.y = imageObject.top + ((newHeight - objectFillSettingsRef.height_to_fill) / 2);
            imageObject.top = initialPoint.y + offset.y;
            break;
          case 'center':
            initialPoint.y = imageObject.top;
            imageObject.top = imageObject.top + offset.y;
            break;
          case 'bottom':
            initialPoint.y = imageObject.top - ((newHeight - objectFillSettingsRef.height_to_fill) / 2);
            imageObject.top = initialPoint.y + offset.y;
            break;
          default:
            break;
        }

        imageObject.initialMovingPoint = initialPoint;
        imageObject.on('modified', function () {
          const preview = window.previews.find(preview => preview.id === this.canvas.instance_id);
          if (!preview) {
            return;
          }
          const objectFillData = preview.data.add_bk_img_object_fill ?? [];
          const backgroundIndex = Number(this.id.split('_').pop());
          objectFillData[backgroundIndex] = {
            ...(objectFillData[backgroundIndex] ?? {}),
            imageOffset: {
              x: this.left - this.initialMovingPoint.x,
              y: this.top - this.initialMovingPoint.y,
            },
          };
          updateLayoutChanges(this.canvas.instance_id, 'add_bk_img_object_fill', objectFillData);
        });
      };

      initObjectFillOrFit();

      if (url && !url.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images)) {
        // this is the "original" URL
        url = deleteURLParameters(url + is_lightweight, ['lightweight', 'scale']);
        original_url = url;

        // use full resolution image for metrics
        url = deleteURLParameters(urls[i], ['scale','lightweight','full_resolution']);
      }

      fabric.Image.fromURL(url, async function (oImg) {
        let bShadow = 0;
        const ovalShadow = canvas_data[id]._data?.add_bk_img_oval_shadows?.[i]?.length ? data?.add_bk_img_oval_shadows?.[i] : null;
        const ovalShadowGroupId = canvas_data[id]._data?.add_bk_img_oval_shadow_group_ids?.length ? data?.add_bk_img__oval_shadow_group_ids?.[i] : null;
        const angle = parseFloat(canvas_data[id]._data.add_bk_img_angle[i]);

        // patch for older layouts in which the vertical scale did not exist
        if ( scale_y == null && canvas_data[id]._data.add_bk_img_scale_y ) {
          scale_y = canvas_data[id]._data.add_bk_img_scale_y[i];
        }

        // keep aspect ratio if width or height are defined.
        if (!dimension_height && dimension_width) {
          dimension_height = dimension_width / (oImg.width / oImg.height);
        }
        if (dimension_height && !dimension_width) {
          dimension_width = dimension_height / (oImg.height / oImg.width);
        }
        // if not set use default image size
        if (!dimension_width) {
          dimension_width = oImg.width;
        }
        if (!dimension_height) {
          dimension_height = oImg.height;
        }

        let isPasteObject = false;
        let isFirstPasteObject = false;

        if (canvas_data[id]._data?.add_bk_img_is_paste_object) {
          isPasteObject = canvas_data[id]._data?.add_bk_img_is_paste_object[i];
          isFirstPasteObject = canvas_data[id]._data?.add_bk_img_is_paste_object.filter(item => item === true).length === 1;
        }

        let isShifted = true;
        if (canvas_data[id]._data?.add_bk_img_is_shifted) {
          isShifted = canvas_data[id]._data?.add_bk_img_is_shifted[i];
        }

        // Get paste scaled dimensions if available (for preserving visual size when pasting)
        const pasteScaledWidth = canvas_data[id]._data?.add_bk_img_paste_scaled_width?.[i] ?? null;
        const pasteScaledHeight = canvas_data[id]._data?.add_bk_img_paste_scaled_height?.[i] ?? null;

        // Calculate the scale values
        const calculatedScaleX = getScale(dimension_width, oImg.width, scale, isPasteObject, pasteScaledWidth, isScaleRelativeToStoredSize);
        const calculatedScaleY = getScale(dimension_height, oImg.height, scale_y, isPasteObject, pasteScaledHeight, isScaleRelativeToStoredSize);
        
        const smartObjectFrame = getSmartObjectFrame(canvas, groupName);
        const smartObjectBounds = getSmartObjectBounds({
          data,
          smartObjectCoords: canvas_data[id].smartObjCoords,
          groupName,
          smartObjectFrame,
        });
        const position = getAdditionalBackgroundPosition({
          left,
          top,
          offsetX: offset_x,
          offsetY: offset_y,
          smartObjectFrame: smartObjectBounds,
        });

        const config = {
          id: objectId,
          element_id: b['real_id'],
          order,
          left: position.left,
          top: position.top,
          originX: 'center',
          originY: 'center',
          scaleX: calculatedScaleX,
          scaleY: calculatedScaleY,
          selectable: b['moveable'],
          evented: b['moveable'],
          isApplyToAll,
          flipX: !!+data?.add_bk_img_flipX?.[i],
          flipY: !!+data?.add_bk_img_flipY?.[i],
          name: settingsTemplate.getNameById(objectId, objectName),
          link: settingsTemplate.getLinkById(objectId, ''),
          fieldType: FIELD_TYPE_BACKGROND_THEME_IMAGE,
          relatedField: b.relatedField,
          opacity,
          visible,
          isClippingMask,
          objectFitSettings: {...objectFitSettingsRef},
          objectFillSettings: {...objectFillSettingsRef},
          groupName,
          lockUniScaling: true,
          // lockUniScaling: true
          ovalShadow,
          ovalShadowGroupId,
          updateLayoutChanges,
          getShadowData,
          setShadowData,
          clearShadowData,
          mirrorShadow,
          mirrorShadowGroupId
        };

        if (b['crop']) {
          config['cropX'] = 0;
          config['cropY'] = 0;
          config['scaleX'] = 1;
          config['scaleY'] = 1;
          config['width'] = dimension_width;
          config['height'] = dimension_height;
        }
        oImg.set(config);

        if (smartObjectFrame) {
          oImg.set({
            clipPath: smartObjectFrame.clipPathArea,
            smartObject: smartObjectFrame,
          });
        }

        // Temporarily hide paste objects until full resolution image is loaded
        // to prevent the "flash" of incorrect size
        const shouldHideUntilLoaded = options.shouldHideUntilLoaded ?? isPasteObject;
        if (shouldHideUntilLoaded) {
          oImg.set({ opacity: 0 });
        }

        if (isPasteObject) {
          if (isShifted) {
            addPasteOffset(oImg, canvas_data, id, i);
          }
        }

        if (
          canvas_data[id]?._data?.add_bk_img_is_another_instance &&
          canvas_data[id]?._data?.add_bk_img_is_another_instance[i]
        ) {
          // updateCopyPasteImage(oImg, dimension, canvas_data, id, i);
          if (isShifted && !isFirstPasteObject) {
            addPasteOffset(oImg, canvas_data, id, i);
          }
        }
        
        if (canvas_data[id]?._data)

        if (data.add_bk_img_shadow !== undefined) {
          bShadow = parseInt(data?.add_bk_img_shadow?.[i]);
        }

        oImg.set({ shadow: getShadowFabricInstance(id, oImg.element_id, shadows, bShadow, oImg.scaleX, oImg.scaleY) });
        oImg.set({ locked: getLockValue(id, oImg.element_id) });

        if (angle) {
          oImg.set({ angle: angle });
        }
        
        scaleControlsCornerSize(oImg);
        
        if (data?.add_bk_img_masks?.[i]?.length) {
          canvas.add(await maskFabricBuilder(data.add_bk_img_masks[i], canvas, oImg));
        } else {
          applyObjectFillWithoutCrop(oImg);
          oImg._file = options?.file || null;
          canvas.add(oImg);
        }
       
        const isMirror = data?.[`add_bk_img_is_mirror`]?.[i] && data?.[`add_bk_img_is_mirror`]?.[i] === 'true';
        if (isMirror) {
          createMirror(oImg, i, canvas, data)
            .then(() => {
              canvas.remove(oImg);
            });
        }
       
        let originalScaledWidth = oImg.getScaledWidth();
        let originalScaledHeight = oImg.getScaledHeight();
        let originalWidth = oImg.width;
        let originalHeight = oImg.height;
        let originalScaleX = oImg.scaleX;
        let originalScaleY = oImg.scaleY;
        oImg.originalWidth = originalWidth;
        oImg.originalHeight = originalHeight;
        oImg.originalScaleX = originalScaleX;
        oImg.originalScaleY = originalScaleY;


        let srcUrl = original_url;

        if (srcUrl.includes('/share?file') && !window.isQA) {
          srcUrl += '&variant=avif';
        }

        await setSrcAsync(oImg, srcUrl);

        // For paste objects on reload (isPasteObject=true but pasteScaledWidth=null),
        // the scale was already correctly saved, so we need to recalculate based on
        // the new image dimensions after setSrcAsync
        if (isPasteObject && pasteScaledWidth === null && !isScaleRelativeToStoredSize) {
          // The saved scale is for the full resolution image
          // After setSrcAsync, oImg.width is the full resolution width
          // So we just use the saved scale directly
          oImg.scaleX = calculatedScaleX;
          oImg.scaleY = calculatedScaleY;
        } else {
          oImg.scaleX = (originalWidth * originalScaleX) / oImg.width;
          oImg.scaleY = (originalHeight * originalScaleY) / oImg.height;
        }
        oImg.setCoords(); // Ensure bounding box updates properly

        if (
          isDisplayBuilderLayer
          && !preserveDimensions
        ) {
          if (displayBuilderLayerRole === 'store') {
            // Store images saved before dimension preservation used a scale
            // relative to the previous image. Recover their full-canvas fit
            // once; subsequent loads retain the saved Edit Layout transform.
            fitStoreBackgroundToCanvas(oImg);
            const offsetXValues = [...(data.add_bk_img_offset_x || [])];
            const offsetYValues = [...(data.add_bk_img_offset_y || [])];
            const angleValues = [...(data.add_bk_img_angle || [])];
            offsetXValues[i] = (oImg.left - left).toFixed(2);
            offsetYValues[i] = (oImg.top - top).toFixed(2);
            angleValues[i] = oImg.angle;
            data.add_bk_img_offset_x = offsetXValues;
            data.add_bk_img_offset_y = offsetYValues;
            data.add_bk_img_angle = angleValues;
            canvas_data[id]._data.add_bk_img_offset_x = offsetXValues;
            canvas_data[id]._data.add_bk_img_offset_y = offsetYValues;
            canvas_data[id]._data.add_bk_img_angle = angleValues;
            updateLayoutChanges(id, 'add_bk_img_offset_x', offsetXValues);
            updateLayoutChanges(id, 'add_bk_img_offset_y', offsetYValues);
            updateLayoutChanges(id, 'add_bk_img_angle', angleValues);
          }

          // Upgrade v1/imported Display Builder layers in place. Their old
          // values use the generic additional-field baseline, so capture the
          // currently rendered size before another redraw can change it.
          const widthValues = [...(data.add_bk_img_width || [])];
          const heightValues = [...(data.add_bk_img_height || [])];
          const scaleValues = [...(data.add_bk_img_scale || [])];
          const scaleYValues = [...(data.add_bk_img_scale_y || [])];
          const preserveValues = [...(data.add_bk_img_preserve_dimensions || [])];

          widthValues[i] = oImg.getScaledWidth();
          heightValues[i] = oImg.getScaledHeight();
          scaleValues[i] = 1;
          scaleYValues[i] = 1;
          preserveValues[i] = true;

          data.add_bk_img_width = widthValues;
          data.add_bk_img_height = heightValues;
          data.add_bk_img_scale = scaleValues;
          data.add_bk_img_scale_y = scaleYValues;
          data.add_bk_img_preserve_dimensions = preserveValues;
          canvas_data[id]._data.add_bk_img_width = widthValues;
          canvas_data[id]._data.add_bk_img_height = heightValues;
          canvas_data[id]._data.add_bk_img_scale = scaleValues;
          canvas_data[id]._data.add_bk_img_scale_y = scaleYValues;
          canvas_data[id]._data.add_bk_img_preserve_dimensions = preserveValues;

          updateLayoutChanges(id, 'add_bk_img_width', widthValues);
          updateLayoutChanges(id, 'add_bk_img_height', heightValues);
          updateLayoutChanges(id, 'add_bk_img_scale', scaleValues);
          updateLayoutChanges(id, 'add_bk_img_scale_y', scaleYValues);
          updateLayoutChanges(id, 'add_bk_img_preserve_dimensions', preserveValues);

          scale = 1;
          scale_y = 1;
        }

        // Restore opacity for paste objects after full resolution is loaded
        if (shouldHideUntilLoaded) {
          oImg.set({ opacity: opacity });
        }

        // Persist the final scale values for paste objects so they survive page reload
        // Only do this once - when pasteScaledWidth has a value (first render after paste)
        if (isPasteObject && pasteScaledWidth !== null) {
          const finalScaleArrayX = canvas_data[id]._data.add_bk_img_scale ? [...canvas_data[id]._data.add_bk_img_scale] : [];
          const finalScaleArrayY = canvas_data[id]._data.add_bk_img_scale_y ? [...canvas_data[id]._data.add_bk_img_scale_y] : [];
          finalScaleArrayX[i] = oImg.scaleX;
          finalScaleArrayY[i] = oImg.scaleY;
          canvas_data[id]._data.add_bk_img_scale = finalScaleArrayX;
          canvas_data[id]._data.add_bk_img_scale_y = finalScaleArrayY;
          updateLayoutChanges(id, 'add_bk_img_scale', finalScaleArrayX);
          updateLayoutChanges(id, 'add_bk_img_scale_y', finalScaleArrayY);

          // Clear the paste scaled dimensions so this block doesn't run again on re-render
          if (canvas_data[id]._data.add_bk_img_paste_scaled_width) {
            const pasteWidthArray = [...canvas_data[id]._data.add_bk_img_paste_scaled_width];
            pasteWidthArray[i] = null;
            canvas_data[id]._data.add_bk_img_paste_scaled_width = pasteWidthArray;
            updateLayoutChanges(id, 'add_bk_img_paste_scaled_width', pasteWidthArray);
          }
          if (canvas_data[id]._data.add_bk_img_paste_scaled_height) {
            const pasteHeightArray = [...canvas_data[id]._data.add_bk_img_paste_scaled_height];
            pasteHeightArray[i] = null;
            canvas_data[id]._data.add_bk_img_paste_scaled_height = pasteHeightArray;
            updateLayoutChanges(id, 'add_bk_img_paste_scaled_height', pasteHeightArray);
          }

          // Clear the isPasteObject flag so on next render/reload it uses normal scale calculation
          if (canvas_data[id]._data.add_bk_img_is_paste_object) {
            const isPasteArray = [...canvas_data[id]._data.add_bk_img_is_paste_object];
            isPasteArray[i] = false;
            canvas_data[id]._data.add_bk_img_is_paste_object = isPasteArray;
            updateLayoutChanges(id, 'add_bk_img_is_paste_object', isPasteArray);
          }
        }

        if (isNeedReRenderCanvas) {
          canvas.renderAll();
        }

        canvas_data[id].additionalBkImgCoords[i] = {
          x: left,
          y: top,
          scaleX: oImg.scaleX / scale,
          scaleY: oImg.scaleY / scale_y,
          originalScaleX: originalScaleX,
          originalScaleY: originalScaleY,
          oImg_scaleX: oImg.scaleX,
          oImg_scaleY: oImg.scaleY,
          scale: scale,
          scale_y: scale_y
        };
        
        if (!canvas.skipRender) {
          reorderObjectCanvas(canvas);
        }
        
        resolve(oImg);
      });
    });

    promises.push(promise);
  };

  return Promise.all(promises);
}

function getScale(dimension, imageDimension, scale, isPasteObject, pasteScaledDimension = null, isScaleRelativeToDimension = false) {
  if (isPasteObject && pasteScaledDimension !== null && imageDimension > 0) {
    // When pasting, use the stored scaled dimension to calculate the correct scale
    // pasteScaledDimension is the visual size we want (e.g., 400px)
    // imageDimension is the actual loaded image size (e.g., 800px)
    // So the scale should be: 400 / 800 = 0.5
    return pasteScaledDimension / imageDimension;
  }

  // A pasted image keeps the absolute scale of the copied object, unless its scale is relative to
  // stored dimensions (see isScaleRelativeToStoredSize).
  if (isPasteObject && !isScaleRelativeToDimension) {
    return scale;
  }

  return (dimension / imageDimension) * scale
}

function updateCopyPasteImage(oImg, dimension, canvas_data, id, i) {
  oImg.scaleToWidth(dimension[0]);
  oImg.scaleToHeight(dimension[1]);
  oImg.set({
    left: DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_offset_x,
    top: DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_offset_y,
  });
  
  // updateCanvasData(canvas_data[id], `add_bk_img_offset_x`, i, oImg.left);
  // updateCanvasData(canvas_data[id], `add_bk_img_offset_y`, i, oImg.top);
  // updateCanvasData(canvas_data[id], `add_bk_img_width`, i, oImg.getScaledWidth());
  // updateCanvasData(canvas_data[id], `add_bk_img_height`, i, oImg.getScaledHeight());
  // updateCanvasData(canvas_data[id], `add_bk_img_scale`, i, Math.max(oImg.scaleX || 1, oImg.scaleY || 1));
  // updateCanvasData(canvas_data[id], `add_bk_img_scale_y`, i, oImg.scaleY || 1);
  updateCanvasData(canvas_data[id], `add_bk_img_is_another_instance`, i, false);
}

function addPasteOffset(oImg, canvas_data, id, i) {
  let offsetX = toFixedFloat(canvas_data[id]._data.add_bk_img_offset_x[i]) + PASTE_OBJECT_OFFSET;
  let offsetY = toFixedFloat(canvas_data[id]._data.add_bk_img_offset_y[i]) + PASTE_OBJECT_OFFSET;

  oImg.set({left: offsetX, top: offsetY});
  updateCanvasData(canvas_data[id], `add_bk_img_offset_x`, i, offsetX);
  updateCanvasData(canvas_data[id], `add_bk_img_offset_y`, i, offsetY);
  updateCanvasData(canvas_data[id], `add_bk_img_is_shifted`, i, false);
}

function updateCanvasData(canvas_data, key, index, value) {
  // initialize the position (add_bk_img_is_shifted) if not set..
  if (!canvas_data._data[key]) canvas_data._data[key] = [];
  canvas_data._data[key][index] = value;
}

function toFixedFloat(value, decimals = 2) {
  return parseFloat(parseFloat(value).toFixed(decimals));
}

export function getShadowData() {
  const shadowCases = ['oval', 'mirror', 'customOutline', 'custom'];
  let result = null;
  const {canvas} = this;
  const preview = window.previews.find(preview => preview.id === canvas.instance_id);
  const prefixId = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? this.element_id : this.id}`
  for (const type of shadowCases) {
    switch (type) {
      case 'oval': {
        const key = this.id.split('_').pop();
        result = preview.data?.[`${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_oval_shadows`]?.[key];
        break;
      }
      case 'mirror': {
        const key = this.id.split('_').pop();
        result = preview.data?.[`${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_mirror_shadows`]?.[key];
        break;
      }
      case 'customOutline': {
        result = preview.data?.[`${prefixId}_custom_outline_shadow`];
        break;
      }
      case 'custom': {
        result = preview.data?.[`${prefixId}_custom_shadow`];
        break;
      }
    }
    if (result) return {type, value: result};
  }
  return null;
}
export function setShadowData({type, value, updateShadowData = true}) {
  const { canvas, id } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;

  const key = id.split('_').pop();
  const prefixId = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? this.element_id : this.id}`
  this.clearShadowData({ updateShadowData });
  switch (type) {
    case 'oval': {
      const ovalShadowKey = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_oval_shadows`
      if (!preview.data?.[ovalShadowKey]) {
        preview.data[ovalShadowKey] = [];
      }
      const copyOvalShadowsData = structuredClone(preview.data[ovalShadowKey]);
      copyOvalShadowsData[key] = value;
      this.ovalShadow = value;
      this.canvas.requestRenderAll();
      if (updateShadowData) {
        this.updateLayoutChanges(preview.id, ovalShadowKey, copyOvalShadowsData);
      }
      break;
    }
    case 'mirror': {
      const mirrorShadowKey = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_mirror_shadows`
      if (!preview.data?.[mirrorShadowKey]) {
        preview.data[mirrorShadowKey] = [];
      }
      const copyMirrorShadowsData = structuredClone(preview.data[mirrorShadowKey]);
      copyMirrorShadowsData[key] = value;
      this.mirrorShadow = value;
      this.canvas.requestRenderAll();
      if (updateShadowData) {
        this.updateLayoutChanges(preview.id, mirrorShadowKey, copyMirrorShadowsData);
      }
      break;
    }
    case 'customOutline': {
      if (updateShadowData) {
        this.updateLayoutChanges(preview.id, `${prefixId}_custom_outline_shadow`, value);
      }
      break;
    }
    case 'custom': {
      this.set('shadow', JSON.parse(value))
      this.canvas.requestRenderAll();
      if (updateShadowData) {
        this.updateLayoutChanges(preview.id, `${prefixId}_custom_shadow`, JSON.stringify(this.shadow))
      }
      break;
    }
    default:
      return false;
  }

  return true;
}

export function clearShadowData({ updateShadowData = true } = {}) {
  const { canvas, id } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;

  const key = id.split('_').pop();
  const prefixId = this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? this.element_id : this.id;

  const ovalShadowKey = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_oval_shadows`;
  if (preview.data?.[ovalShadowKey]) {
    const copyOvalShadowsData = structuredClone(preview.data[ovalShadowKey]);
    delete copyOvalShadowsData[key];
    this.ovalShadow = null;
    if (updateShadowData) {
      this.updateLayoutChanges(preview.id, ovalShadowKey, copyOvalShadowsData);
    }
  }

  const mirrorShadowKey = `${this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) ? 'add_' : ''}bk_img_mirror_shadows`;
  if (preview.data?.[mirrorShadowKey]) {
    const copyMirrorShadowsData = structuredClone(preview.data[mirrorShadowKey]);
    delete copyMirrorShadowsData[key];
    this.mirrorShadow = null;
    if (updateShadowData) {
      this.updateLayoutChanges(preview.id, mirrorShadowKey, copyMirrorShadowsData);
    }
  }

  const outlineKey = `${prefixId}_custom_outline_shadow`;
  if (preview.data?.[outlineKey]) {
    delete preview.data[outlineKey];
    if (updateShadowData) {
      this.updateLayoutChanges(preview.id, outlineKey, null);
    }
  }

  const customKey = `${prefixId}_custom_shadow`;
  if (preview.data?.[customKey]) {
    delete preview.data[customKey];
    this.set('shadow', null);
    if (updateShadowData) {
      this.updateLayoutChanges(preview.id, customKey, null);
    }
  }

  this.canvas.requestRenderAll();
  return true;
}
